﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import {
    IndividualIdentifierTypes,
    OrganizationIdentifierTypes,
} from "~base/Internationalization/ConfigurationInfo";
import * as sessionModel from "~base/Services/API/Session/Model";
import {
    getSubjectIdentifierConType,
    getSubjectType,
} from "~base/Types/IdentifierConverter";

//-------------------------------------------------------------------------------------
// Exports
//-------------------------------------------------------------------------------------

export function getIdentifierTypesForModels(
    allowedSubjectIdentifierTypes: string[]
): [IndividualIdentifierTypes[], OrganizationIdentifierTypes[]] {
    const individualTypes = new Array<IndividualIdentifierTypes>();
    const orgTypes = new Array<OrganizationIdentifierTypes>();

    if (allowedSubjectIdentifierTypes) {
        for (const i in allowedSubjectIdentifierTypes) {
            switch (allowedSubjectIdentifierTypes[i]) {
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Personnummer.SWE":
                    individualTypes.push("PersonnummerSE");
                    break;
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Epost":
                    individualTypes.push("Email");
                    break;
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson.Organisationsnummer.SWE":
                    orgTypes.push("OrganisationsnummerSE");
                    break;
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson.Organisationsnummer.NL":
                    orgTypes.push("OrganisationsnummerNL");
                    break;
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.ExterntRegisterId":
                    individualTypes.push("ExterntRegisterId");
                    break;
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPersonPar.Personnummer.SWE":
                    individualTypes.push("ParPersonnummerSE");
                    break;
                case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPersonPar.ExterntRegisterId":
                    individualTypes.push("ParExterntRegisterId");
                    break;
                default:
            }
        }
    }

    return [individualTypes, orgTypes];
}

export function getSessionModelForSubjectTypes(
    matchingSubjectTypes: (
        | IndividualIdentifierTypes
        | OrganizationIdentifierTypes
    )[],
    sessionModels: sessionModel.GetStartInfoSessionModel[]
): sessionModel.GetStartInfoSessionModel[] {
    const filteredModels = new Array<sessionModel.GetStartInfoSessionModel>();

    for (const i in sessionModels) {
        const sessionModel = sessionModels[i];

        for (const j in sessionModel.SessionModelSubjectIdentifiers) {
            const sessionModelSubjectIdentifier =
                sessionModel.SessionModelSubjectIdentifiers[j];

            const supportedIdType = getSubjectType(
                sessionModelSubjectIdentifier
            );
            if (matchingSubjectTypes.some((x) => x == supportedIdType)) {
                filteredModels.push(sessionModel);
                continue;
            }
        }
    }

    return filteredModels;
}

export function getExpectedSubjectType(
    matchingSubjectTypes: Array<
        IndividualIdentifierTypes | OrganizationIdentifierTypes
    >,
    sessionModels: sessionModel.GetStartInfoSessionModel[]
): string {
    for (const i in sessionModels) {
        const sm = sessionModels[i];

        if (
            sm.SessionModelSubjectType ==
            "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson"
        ) {
            if (matchingSubjectTypes.some((x) => x == "OrganisationsnummerSE"))
                return getSubjectIdentifierConType("OrganisationsnummerSE");
            if (matchingSubjectTypes.some((x) => x == "OrganisationsnummerNL"))
                return getSubjectIdentifierConType("OrganisationsnummerNL");
            if (matchingSubjectTypes.some((x) => x == "PersonnummerSE"))
                return getSubjectIdentifierConType("PersonnummerSE");
        }
        if (
            sm.SessionModelSubjectType ==
            "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson"
        ) {
            if (matchingSubjectTypes.some((x) => x == "PersonnummerSE"))
                return getSubjectIdentifierConType("PersonnummerSE");
            if (matchingSubjectTypes.some((x) => x == "Email"))
                return getSubjectIdentifierConType("Email");
        }
        if (
            sm.SessionModelSubjectType ==
            "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPersonPar"
        ) {
            if (matchingSubjectTypes.some((x) => x == "ParPersonnummerSE"))
                return getSubjectIdentifierConType("ParPersonnummerSE");
            if (matchingSubjectTypes.some((x) => x == "ParExterntRegisterId"))
                return getSubjectIdentifierConType("ParExterntRegisterId");
        }
    }
    return null;
}

export const sortSessionItemsDescending = (
    x: sessionModel.Session,
    y: sessionModel.Session
): number =>
    new Date(y?.creationDate).getTime() - new Date(x?.creationDate).getTime();
